// ***************************************************************************************
// What a tap on a tile means.
//
// Pulled out as a pure function on purpose. Every input bug in this project so far has
// been in exactly this kind of logic - a rule that is easy to state, easy to get subtly
// wrong, and invisible to a test that drives some other path. Stating it once, here,
// means it can be tested without a browser, a canvas or React.
//
// The rules, in order:
//
//   nothing selected        -> start a new word
//   the tile already at the end, and the word is playable   -> SUBMIT
//   the tile already at the end, and the word is not        -> remove it
//   the tile already in the word somewhere earlier          -> CUT BACK TO IT
//   not adjacent to the end -> start a new word from it
//   otherwise               -> extend
//
// The two "already at the end" cases are the interesting pair. Pressing the last letter
// again is the fastest way to play a word by tapping, and a finished word has nowhere
// else to go - but when the word is NOT playable, the same press has to do something
// useful rather than nothing, and taking the letter back is what a player means by it.
//
// CUTTING BACK is what pressing any EARLIER letter means. With WINDIAR selected, pressing
// the D leaves WIND - the letter you pressed is kept and everything after it goes. That
// beats the alternatives: ignoring the press leaves a player who can see their mistake
// with no way to say so but backspace-backspace-backspace, and restarting from that tile
// throws away the part of the word they were happy with.
// ***************************************************************************************

export interface Tile {
  readonly x: number;
  readonly y: number;
}

export type TapAction =
  | { kind: 'start'; tile: Tile }
  | { kind: 'extend'; tile: Tile }
  | { kind: 'remove-last' }
  /** Keep the first `length` tiles and drop the rest. */
  | { kind: 'truncate'; length: number }
  | { kind: 'submit' };

function same(a: Tile, b: Tile): boolean {
  return a.x === b.x && a.y === b.y;
}

function adjacent(a: Tile, b: Tile): boolean {
  return Math.abs(a.x - b.x) <= 1 && Math.abs(a.y - b.y) <= 1;
}

/**
 * @param selection the tiles currently selected, in order
 * @param tile      the tile that was tapped
 * @param playable  whether the CURRENT selection is a word the game would accept
 * @param adjacency whether letters have to touch - false in Freedom, where "not next to
 *                  the last one" is not a reason to do anything different
 */
export function tapDecision(
  selection: readonly Tile[],
  tile: Tile,
  playable: boolean,
  adjacency: boolean = true,
): TapAction {
  if (selection.length === 0) return { kind: 'start', tile };

  const last = selection[selection.length - 1]!;

  if (same(last, tile)) {
    return playable ? { kind: 'submit' } : { kind: 'remove-last' };
  }

  // Somewhere earlier in the word: cut back to it. The pressed tile is KEPT - it is the
  // letter the player pointed at, so it is the one they mean to end on.
  const at = selection.findIndex((selected) => same(selected, tile));

  if (at >= 0) return { kind: 'truncate', length: at + 1 };

  // Starting a new word from a distant tile is the RIGHT answer when letters must touch:
  // the player has reached somewhere the current word cannot go, so they must mean a new
  // one. In Freedom it would be exactly wrong - every tile is reachable, and throwing the
  // word away because the next letter happens to be across the board is a bug the player
  // would have to learn to work around.
  if (adjacency && !adjacent(last, tile)) return { kind: 'start', tile };

  return { kind: 'extend', tile };
}

// ***************************************************************************************
// What DRAGGING onto a tile means.
//
// The same shape as a tap and, deliberately, the same cut-back rule: a finger that slides
// back over a letter already in the word ends the word there. Dragging back one step is
// the everyday case - correcting a mis-drag - but a finger moving quickly can skip a tile,
// and a gesture that crossed two at once should land where a slower one would.
//
// Unlike a tap, a drag never starts a new word and never submits. It is one continuous
// gesture: a jump to a tile that is not adjacent is a finger that has left the path, and
// the selection simply stays where it was.
// ***************************************************************************************
export function extendSelection(
  selection: readonly Tile[],
  tile: Tile,
  adjacency: boolean = true,
): readonly Tile[] {
  if (selection.length === 0) return [tile];

  const last = selection[selection.length - 1]!;

  if (same(last, tile)) return selection;

  const at = selection.findIndex((selected) => same(selected, tile));

  if (at >= 0) return selection.slice(0, at + 1);

  // Without adjacency a drag is just a quick way to touch several tiles, so every tile
  // the finger crosses joins the word. A gap in the trail is not a mistake to guard
  // against - the letters never had to be neighbours.
  if (adjacency && !adjacent(last, tile)) return selection;

  return [...selection, tile];
}
